// ============================================================================
// GST ENGAGEMENT TYPES — G-1 slice 1
// ============================================================================

/**
 * One filing-cadence election for a GSTIN — effective from a quarter,
 * continuing until a later election supersedes it (ADR-0019).
 *
 * QRMP is elected per QUARTER on the portal and then continues, so this is not
 * an annual setting. A client who never switches has exactly one of these.
 */
export interface GstFilingOption {
  id: string;
  registrationId: string;

  /** First day of the quarter it takes effect: 1 Apr / Jul / Oct / Jan. */
  effectiveFromQuarter: string;

  filingFrequency: 'MONTHLY' | 'QRMP';

  /** IFF absent under QRMP means the client opted out of it. */
  returnTypes: string[];

  notes?: string | null;
  rowVersion: number;
}

/** One GSTIN in scope for a GST engagement, with its settings for that year. */
export interface GstEngagementRegistration {
  id: string;
  registrationId: string;
  clientRegistrationId: string;

  /** Read through prima.tbl_client_registration — GST stores no copy. */
  gstin: string;

  tradeName?: string | null;
  jurisdiction?: string | null;

  /** Derived from the GSTIN's first two characters, not stored. */
  stateCode: string;

  registrationType: string;

  /**
   * Cadence in force at the START of the engagement's financial year.
   *
   * A convenience for the common case — one election, all year. NOT the whole
   * truth when the client switched mid-year, so anything that must be right per
   * period reads `filingOptions` instead. See ADR-0019.
   */
  filingFrequency: 'MONTHLY' | 'QRMP';

  /** 4, 6 or 8 — follows the client's aggregate turnover for the year. */
  requiredHsnDigits: number;

  /** Return set of the election in force at the start of the year. */
  returnTypes: string[];

  /**
   * Every cadence election effective during this financial year, oldest first.
   * More than one means the client switched between monthly and QRMP at a
   * quarter boundary — the case a single per-year frequency cannot express.
   */
  filingOptions: GstFilingOption[];

  scopeStatus: 'IN_SCOPE' | 'WITHDRAWN';
  scopeFrom?: string | null;
  scopeTo?: string | null;
  gstnVerifiedAt?: string | null;
  gstnStatus?: string | null;
  rowVersion: number;
}

export interface GstEngagement {
  id: string;
  primaEngagementId: string;
  engagementCode?: string | null;

  /**
   * The engagement type the USER chose. GST owns no type of its own, so this
   * can be any type the tenant maintains — including one they created before
   * the module existed.
   */
  engagementTypeCode?: string | null;
  engagementTypeName?: string | null;

  clientId: string;

  /** Statutory name — what appears on returns. Never the display name. */
  clientLegalName?: string | null;
  clientName?: string | null;

  financialYear: number;
  dataSourceMode: string;
  filingNotes?: string | null;

  registrations: GstEngagementRegistration[];
  registrationCount: number;

  statusId: string;
  rowVersion: number;
  createdAt?: string | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/**
 * A client GSTIN that could be brought into scope, sourced from the client
 * master. Adding a GSTIN remains a client-master action — GST never invents
 * registrations.
 */
export interface AvailableGstRegistration {
  clientRegistrationId: string;
  gstin: string;
  jurisdiction?: string | null;
  registrationDate?: string | null;
  validTo?: string | null;
  stateCode: string;

  /** GST already extends this master row; the extension is reused, not duplicated. */
  hasGstExtension: boolean;

  registrationId?: string | null;
  tradeName?: string | null;
  registrationType?: string | null;

  /** Already in scope for the engagement being edited — offered as taken. */
  alreadyInScope: boolean;
}

export interface CreateGstEngagementRegistrationRequest {
  clientRegistrationId: string;
  tradeName?: string | null;
  registrationType: string;
  filingFrequency: 'MONTHLY' | 'QRMP';
  requiredHsnDigits: number;
  returnTypes: string[];
  scopeFrom?: string | null;
}

export interface CreateGstEngagementRequest {
  clientId: string;

  /** User-chosen. GST supplies no default type of its own. */
  engagementTypeId: string;

  financialYear: number;

  /** A real employee — a JWT user id is not an employee id. */
  managerId: string;

  engagementCode?: string | null;
  description?: string | null;
  dataSourceMode: string;
  filingNotes?: string | null;
  registrations: CreateGstEngagementRegistrationRequest[];
}

export interface GstEngagementSearchRequest {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  financialYear?: number;
  clientId?: string;
}

/**
 * One filing obligation — a return type for a tax period, for one GSTIN.
 *
 * The lifecycle half (`workflowStateCode`) is JOINED from the EWF instance,
 * never stored by GST, so it cannot disagree with the engine.
 */
export interface GstFilingPeriod {
  id: string;
  clientRegistrationId?: string | null;
  gstin: string;
  tradeName?: string | null;
  financialYear: number;
  returnType: string;
  taxPeriodStart: string;
  taxPeriodEnd: string;

  /** MONTHLY | QUARTERLY | ANNUAL — the period SHAPE (ADR-0020). */
  cadence: string;

  /** MONTHLY | QRMP — the client's SCHEME when generated (ADR-0019). */
  filingFrequency: string;

  /** Derived from the dates by the API, not stored: "Apr 2026" / "Apr-Jun 2026". */
  periodLabel: string;

  /** The statutory date. Null when no rule covers this return type. */
  dueDate?: string | null;

  /** What a notification moved it to. Null normally. */
  extendedDueDate?: string | null;

  /** The date that actually applies — what the list sorts on. */
  effectiveDueDate?: string | null;

  /**
   * Derived server-side from filed_at and the effective date, never stored —
   * so an extension recorded AFTER a filing corrects this automatically
   * (ADR-0021 §6).
   */
  isLate?: boolean;

  workflowInstanceId?: string | null;
  workflowStateCode?: string | null;
  workflowStateName?: string | null;

  arn?: string | null;
  filedAt?: string | null;
  filingMode?: string | null;
  rowVersion: number;
}

/** What a generation run actually did — a no-op run is visibly a no-op. */
export interface GstPeriodGenerationResult {
  gstEngagementSettingsId: string;
  financialYear: number;
  created: number;
  alreadyPresent: number;
  /** Periods left with no statutory date because no rule covers the form. */
  withoutDueDate?: number;
  /** GSTINs skipped because they carry no cadence election at all. */
  skippedWithoutElection: string[];
}

/** Outcome of installing the six GST workflow definitions for this tenant. */
export interface GstWorkflowInstallResult {
  installed: string[];
  alreadyInstalled: string[];
}

// ============================================================================
// EDITING AN ENGAGEMENT (G-1 slice 2f)
// ============================================================================
// Generation is additive and re-runnable by design, so WIDENING scope simply
// creates what is newly owed. NARROWING it strands obligations — and some are
// already filed, which no later edit undoes — so the API reports what fell
// outside rather than removing anything.

/** Client, financial year and engagement are identity and are absent by design. */
export interface UpdateGstEngagementRequest {
  dataSourceMode: string;
  /** Never credentials — the firm does not store client portal logins. */
  filingNotes?: string | null;
  rowVersion: number;
}

/** Brings a GSTIN into scope mid-year — the commonest real edit. */
export interface AddGstEngagementRegistrationRequest {
  clientRegistrationId: string;
  tradeName?: string | null;
  registrationType: string;
  /** The cadence it starts on, recorded as its first quarter-effective election. */
  filingFrequency: string;
  requiredHsnDigits: number;
  returnTypes: string[];
  /** When it entered scope. Null means the start of the financial year. */
  scopeFrom?: string | null;
}

/** Corrects a scope row's per-year settings. Cadence is elected separately. */
export interface UpdateGstEngagementRegistrationRequest {
  requiredHsnDigits: number;
  scopeFrom?: string | null;
  /** Narrowing this is what strands obligations beyond the new end. */
  scopeTo?: string | null;
  rowVersion: number;
}

/** Takes a GSTIN out of scope from a date. Never deletes. */
export interface WithdrawGstEngagementRegistrationRequest {
  /** The last day it was in scope. Required — without it, nothing can say which periods stand. */
  scopeTo: string;
  reason?: string | null;
  rowVersion: number;
}

/**
 * Records a cadence election effective from a quarter (ADR-0019).
 * An ADDITION, never an edit: the superseded row still governs what it governed.
 */
export interface AddGstFilingOptionRequest {
  /** First day of a quarter — 1 Apr, 1 Jul, 1 Oct or 1 Jan. */
  effectiveFromQuarter: string;
  filingFrequency: string;
  returnTypes: string[];
  notes?: string | null;
}

/** An obligation that no longer belongs after a scope or cadence change. */
export interface GstOutOfScopeObligation {
  obligationId: string;
  workflowInstanceId?: string | null;
  registrationNumber?: string | null;
  formCode: string;
  periodLabel: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  workflowStateCode?: string | null;
  filedAt?: string | null;
  /**
   * Cancellable — unfiled and unstarted.
   * InProgress — started; cancellable, but never by reflex.
   * Filed — listed and never offered for cancellation.
   */
  verdict: 'Cancellable' | 'InProgress' | 'Filed';
  /** Which edge the period fell off, in words. */
  reason?: string | null;
}

/** What a scope or cadence change left behind. */
export interface GstScopeReconciliation {
  settingsId: string;
  outOfScope: GstOutOfScopeObligation[];
  cancellable: number;
  inProgress: number;
  /** Already filed. Shown so the change is visible; never actionable. */
  filed: number;
}

/**
 * One quarter a filing-frequency change could take effect from.
 *
 * A change IS effective-dated — moving to QRMP in July elects from 1 July and
 * leaves April to June monthly. What is refused is electing from a quarter whose
 * return has already been FILED, since that would re-shape a period already
 * submitted to the department.
 */
export interface GstElectableQuarter {
  effectiveFromQuarter: string;
  label: string;
  selectable: boolean;
  /** Null when selectable; otherwise which filed return blocks it. */
  reason?: string | null;
}

/** What a bulk cancellation actually did — including what it deliberately did not. */
export interface GstBulkCancelResult {
  cancelled: number;
  /** Cancellations that threw. The rest still went through. */
  failed: number;
  /** Left alone because they were filed. Never cancellable. */
  filed: number;
  /** Left alone because `includeInProgress` was not set. */
  skippedInProgress: number;
  /** What remains stranded afterwards. */
  reconciliation: GstScopeReconciliation;
}

/**
 * What recording a filing frequency did.
 *
 * `changed` is not decoration: the upsert writes nothing when the frequency
 * asked for is already the one in force, and reporting that as a save is exactly
 * the failure this path had.
 */
export interface GstFilingOptionResult {
  changed: boolean;
  /** The periods the new frequency owes. Absent when nothing changed. */
  generation?: GstPeriodGenerationResult | null;
  /** What the old frequency left behind. */
  reconciliation: GstScopeReconciliation;
}

// ---- Source documents (ADR-0025 §3) ----------------------------------------

/**
 * One received artefact — the immutable head of the traceability chain. The
 * bytes live in object storage and are reached through the download endpoint.
 */
export interface GstSourceDocument {
  id: string;
  gstEngagementSettingsId: string;
  /** Exactly as the client named it. Display only — never consulted for anything. */
  fileName: string;
  /** XLSX | XLS | CSV | DOCX | PDF | JSON | UNKNOWN — from the bytes, never the extension. */
  sniffedFormat: string;
  contentType?: string | null;
  byteSize: number;
  sha256: string;
  /** What the uploader said the file is. See GST_SOURCE_DOCUMENT_KINDS. */
  declaredKind: string;
  receivedVia: string;
  receivedAt: string;
  isMachineReadable: boolean;
  /** RECEIVED | PARSED | FAILED | UNREADABLE. */
  parseStatus: string;
  parseError?: string | null;
  parsedAt?: string | null;
  /** The earlier receipt of identical bytes under this engagement, if any. */
  duplicateOfSourceDocumentId?: string | null;
  notes?: string | null;
  createdByName?: string | null;
  createdAt?: string | null;
  rowVersion: number;
  /** Live submissions this document has been parsed into. Empty = not parsed yet. */
  feeds: GstDocumentFeed[];
  /** Which expected file it was received as; null for "something else". */
  expectedFileId?: string | null;
  expectedFileName?: string | null;
  /** RECEIPT | SAMPLE — samples are for source setup only. */
  purpose: 'RECEIPT' | 'SAMPLE';
}

export interface GstSourceDocumentSearchRequest {
  page?: number;
  pageSize?: number;
  search?: string;
  declaredKind?: string;
  parseStatus?: string;
}

/**
 * The vocabulary for `declaredKind`, grouped the way the uploader thinks about
 * files. Mirrors `GstSourceDocumentKinds` on the server; the server validates.
 */
// ---- Submissions and staging (ADR-0012 §1, ADR-0025 §3, §4, §8) -------------

/**
 * The stream a submission feeds — one per return table or register. Mirrors
 * `GstDocumentTypes` on the server and the CHECK constraints in the database.
 */
export const GST_DOCUMENT_TYPES: ReadonlyArray<{ value: string; label: string; group: 'Outward' | 'Inward' }> = [
  { value: 'OUTWARD_SALES', label: 'Sales register', group: 'Outward' },
  { value: 'OUTWARD_SALES_RETURN', label: 'Sales return', group: 'Outward' },
  { value: 'OUTWARD_CREDIT_NOTE', label: 'Credit notes issued', group: 'Outward' },
  { value: 'OUTWARD_DEBIT_NOTE', label: 'Debit notes issued', group: 'Outward' },
  { value: 'HSN_SUMMARY', label: 'HSN summary', group: 'Outward' },
  { value: 'B2B_TABLE', label: 'B2B table (already classified)', group: 'Outward' },
  { value: 'B2CS_TABLE', label: 'B2CS table (already classified)', group: 'Outward' },
  { value: 'B2CL_TABLE', label: 'B2CL table (already classified)', group: 'Outward' },
  { value: 'DOCUMENT_SERIES', label: 'Document series', group: 'Outward' },
  { value: 'INWARD_PURCHASE', label: 'Purchase register', group: 'Inward' },
  { value: 'INWARD_PURCHASE_RETURN', label: 'Purchase return', group: 'Inward' },
  { value: 'INWARD_CREDIT_NOTE', label: 'Credit notes received', group: 'Inward' },
  { value: 'INWARD_DEBIT_NOTE', label: 'Debit notes received', group: 'Inward' },
];

/** The stream a declared document kind normally feeds; null for container kinds, where the user picks. */
export const defaultDocumentTypeFor = (declaredKind: string): string | null => {
  switch (declaredKind) {
    case 'SALES_REGISTER': return 'OUTWARD_SALES';
    case 'SALES_RETURN': return 'OUTWARD_SALES_RETURN';
    case 'B2B': return 'B2B_TABLE';
    case 'B2CS': return 'B2CS_TABLE';
    case 'B2CL': return 'B2CL_TABLE';
    case 'HSN_SUMMARY': return 'HSN_SUMMARY';
    case 'CREDIT_NOTE_OUTWARD': return 'OUTWARD_CREDIT_NOTE';
    case 'DEBIT_NOTE_OUTWARD': return 'OUTWARD_DEBIT_NOTE';
    case 'PURCHASE_REGISTER': return 'INWARD_PURCHASE';
    case 'PURCHASE_RETURN': return 'INWARD_PURCHASE_RETURN';
    case 'CREDIT_NOTE_INWARD': return 'INWARD_CREDIT_NOTE';
    case 'DEBIT_NOTE_INWARD': return 'INWARD_DEBIT_NOTE';
    default: return null;
  }
};

export interface GstSheetStats {
  name: string;
  headerRow?: number | null;
  headerCells: string[];
  title: number;
  header: number;
  data: number;
  continuation: number;
  trailer: number;
  ignored: number;
}

export interface GstParseStats {
  rows: number;
  data: number;
  sheets: GstSheetStats[];
}

/** One (GSTIN, period, branch, stream), parsed from one received document. */
export interface GstDataSubmission {
  id: string;
  gstEngagementSettingsId: string;
  registrationId: string;
  gstin?: string | null;
  taxPeriodStart: string;
  taxPeriodEnd: string;
  sourceRef: string;
  documentType: string;
  submissionNo: number;
  supersedesSubmissionId?: string | null;
  scope: string;
  sourceDocumentId: string;
  sourceDocumentFileName?: string | null;
  mappingProfileId?: string | null;
  sheetNames: string[];
  summaryBasis?: string | null;
  /** RECEIVED | PARSED | NORMALISED | ACCEPTED | SUPERSEDED | REJECTED. */
  submissionStatus: string;
  revisionReason?: string | null;
  revisionNote?: string | null;
  receivedAt: string;
  parseStats?: GstParseStats | null;
  acceptedAt?: string | null;
  acceptedByName?: string | null;
  createdByName?: string | null;
  rowVersion: number;
}

export interface GstSubmissionSearchRequest {
  page?: number;
  pageSize?: number;
  sourceDocumentId?: string;
  registrationId?: string;
  documentType?: string;
  submissionStatus?: string;
  includeInactive?: boolean;
}

export interface CreateGstSubmissionRequest {
  sourceDocumentId: string;
  registrationId: string;
  taxPeriodStart: string;
  taxPeriodEnd: string;
  sourceRef: string;
  documentType: string;
  sheetNames: string[];
  summaryBasis?: string | null;
  confirmSupersede: boolean;
  revisionReason?: string | null;
  revisionNote?: string | null;
}

/** A sheet as found in a received file, before any submission exists. */
export interface GstSheetInfo {
  name: string;
  rows: number;
  columns: number;
  isHidden: boolean;
  hasTable: boolean;
  headerRow?: number | null;
  headerCells: string[];
  dataRows: number;
  /** The stream the dialog pre-selects; null = skip. A suggestion only. */
  suggestedDocumentType?: string | null;
}

// ---- Mapping (ADR-0025 §5) ----------------------------------------------------

export interface GstMappingProfile {
  id: string;
  clientId?: string | null;
  /** SYSTEM | LEARNED. */
  profileKind: string;
  documentType: string;
  name: string;
  version: number;
  profileStatus: string;
  /** TRANSACTION | RETURN_TABLE. */
  sourceGrain: string;
  summaryBasis?: string | null;
  notes?: string | null;
  definition: string;
  supersedesProfileId?: string | null;
  createdAt?: string | null;
  createdByName?: string | null;
  acceptedAt?: string | null;
  acceptedByName?: string | null;
  rejectionReason?: string | null;
}

/** Saves the mapper's definition as the client's LEARNED profile, PROPOSED for review. */
export interface SaveGstMappingProfileRequest {
  /** Required for a new profile; ignored for a new version, which keeps its name. */
  name?: string | null;
  definition: string;
  notes?: string | null;
  /** GROSS | NET — HSN summary streams only. */
  summaryBasis?: string | null;
  supersedesProfileId?: string | null;
  useOnSubmission?: boolean;
}

// ---- Expected files and completeness (ADR-0025 §7, Revision 1) --------------

/** A file the client sends each period for one GSTIN and branch, with the streams it feeds. */
export interface GstExpectedFile {
  id: string;
  engagementRegistrationId: string;
  registrationId: string;
  gstin?: string | null;
  sourceRef: string;
  displayName: string;
  /** Source-document kind — SALES_REGISTER … ACCOUNTING_GST_REPORT. */
  fileKind: string;
  isMandatory: boolean;
  notes?: string | null;
  rowVersion: number;
  /** Empty for a container kind until its first file is received. */
  streams: GstExpectedSource[];
  /** Every stream knows its sheets: receiving parses with no dialog. */
  learned: boolean;
  /** What source setup decided: part → PRIMARY | CROSS_CHECK. */
  partRoles: Record<string, 'PRIMARY' | 'CROSS_CHECK'>;
}

export interface SaveGstExpectedFileRequest {
  registrationId: string;
  sourceRef: string;
  displayName: string;
  fileKind: string;
  isMandatory: boolean;
  notes?: string | null;
  rowVersion?: number;
}

// ---- Source setup (ADR-0025 R1.4) --------------------------------------------

export type GstReturnPart = 'B2B' | 'B2C' | 'CDN' | 'HSN' | 'DOCS';

export interface GstSampleParse {
  submissionId: string;
  registrationId: string;
  gstin?: string | null;
  documentType: string;
  sheetNames: string[];
  taxPeriodStart: string;
  taxPeriodEnd: string;
  dataRows: number;
  mappingProfileId?: string | null;
}

export interface GstSampleFile {
  document: GstSourceDocument;
  parses: GstSampleParse[];
}

export interface GstCoverage {
  part: GstReturnPart;
  documentId: string;
  label: string;
  derived: boolean;
  level: 'FULL' | 'PARTIAL';
  records: number;
  invoices: number;
  taxable: number;
  tax: number;
  missingFields: { field: string; fieldName: string; missing: number; of: number }[];
}

export interface GstComparison {
  part: GstReturnPart;
  labelA: string;
  labelB: string;
  agree: boolean;
  invoicesA: number;
  invoicesB: number;
  onlyInA: number;
  onlyInB: number;
  onlyInAExamples: string[];
  onlyInBExamples: string[];
  rates: { rate?: number | null; taxableA: number; taxableB: number; taxA: number; taxB: number; agrees: boolean }[];
  hsnDiffs: { hsn: string; taxableA: number; taxableB: number }[];
  notes: string[];
}

export interface GstPartRecommendation {
  part: GstReturnPart;
  primaryDocumentId: string;
  crossCheckDocumentIds: string[];
  duplicateDocumentIds: string[];
  needsDecision: boolean;
  reason: string;
}

export interface GstSourceAnalysis {
  registrationId: string;
  gstin?: string | null;
  files: { documentId: string; fileName: string; streams: string[]; profiles: string[]; parts: GstReturnPart[]; suggestedKind: string }[];
  coverage: GstCoverage[];
  comparisons: GstComparison[];
  recommendations: GstPartRecommendation[];
  gaps: { part: string; severity: 'INFO' | 'WARNING' | 'ERROR'; message: string }[];
  unmapped: GstSampleParse[];
}

export interface FinaliseGstSourceSetupRequest {
  registrationId: string;
  files: { documentId: string; include: boolean; sourceRef: string; displayName: string; fileKind: string; isMandatory: boolean }[];
  parts: { part: GstReturnPart; primaryDocumentId: string; crossCheckDocumentIds: string[]; evidence?: string | null }[];
}

export interface GstSuggestedPeriod {
  taxPeriodStart: string;
  taxPeriodEnd: string;
  /** False when every month so far is in; the suggestion is then the current month. */
  outstanding: boolean;
}

/** A stream of an expected file: which return stream it feeds, from which sheets. */
export interface GstExpectedSource {
  id: string;
  expectedFileId?: string | null;
  /** Sheet-name patterns (case-insensitive, * wildcard); empty until learned. */
  sheetPatterns: string[];
  engagementRegistrationId: string;
  registrationId: string;
  gstin?: string | null;
  sourceRef: string;
  displayName: string;
  documentType: string;
  isMandatory: boolean;
  /** Learned from the source's first mapped file; null until then. */
  mappingProfileId?: string | null;
  mappingProfileName?: string | null;
  mappingProfileVersion?: number | null;
  notes?: string | null;
  rowVersion: number;
}

/** RECEIVED | WAIVED | PARTIAL | MISSING. */
export type GstSourceStatusCode = 'RECEIVED' | 'WAIVED' | 'PARTIAL' | 'MISSING';

/** One stream's standing for a period. documentType "*" = a file whose streams are not learned yet. */
export interface GstSourceStatus {
  sourceId: string;
  sourceRef: string;
  displayName: string;
  documentType: string;
  isMandatory: boolean;
  status: GstSourceStatusCode;
  missingMonths: string[];
  submissions: { submissionId: string; submissionNo: number; taxPeriodStart: string; taxPeriodEnd: string; submissionStatus: string }[];
}

/** One expected file's standing for a period, with its streams. */
export interface GstFileStatus {
  fileId: string;
  sourceRef: string;
  displayName: string;
  isMandatory: boolean;
  status: GstSourceStatusCode;
  missingMonths: string[];
  streams: GstSourceStatus[];
  waiver?: { id: string; taxPeriodStart: string; taxPeriodEnd: string; reason: string; waivedByName?: string | null; waivedAt?: string | null } | null;
}

/** What receiving a file did (ADR-0025 R1.2). */
export type GstReceiveOutcome = 'RECEIVED' | 'PARSED' | 'NEEDS_SETUP' | 'NEEDS_CONFIRMATION' | 'ALREADY_PARSED' | 'NOT_READABLE';

export interface GstReceivedSubmission {
  submissionId: string;
  submissionNo: number;
  documentType: string;
  dataRows: number;
  /** Null when no profile fits yet — map it in the preview. */
  profileName?: string | null;
  records: number;
  recordsWithErrors: number;
  recordsWithWarnings: number;
  byRate: GstRateTotal[];
}

export interface GstReceiveResult {
  receipt: { document: GstSourceDocument; alreadyReceived: boolean; declaredKindOnRecord?: string | null };
  outcome: GstReceiveOutcome;
  submissions: GstReceivedSubmission[];
  messages: { sheet?: string | null; severity: string; message: string }[];
  registrationId?: string | null;
  sourceRef?: string | null;
  taxPeriodStart?: string | null;
  taxPeriodEnd?: string | null;
  /** Sheet → stream as far as the file's learned layout says. */
  sheetStreams: Record<string, string>;
}

export interface GstPeriodCompleteness {
  obligationId: string;
  registrationId: string;
  gstin?: string | null;
  returnType: string;
  taxPeriodStart: string;
  taxPeriodEnd: string;
  mandatoryExpected: number;
  mandatoryReady: number;
  sourcesDeclared: boolean;
  items: GstFileStatus[];
}

export interface GstPeriodCompletenessSummary {
  obligationId: string;
  mandatoryExpected: number;
  mandatoryReady: number;
  optionalExpected: number;
  optionalReceived: number;
  sourcesDeclared: boolean;
}

/** One field of the mapping vocabulary. */
export interface GstMappingField {
  name: string;
  /** Text | Date | Decimal | Gstin | Hsn | StateCode | YesNo. */
  type: string;
  isAmount: boolean;
}

export interface GstProfileCandidate {
  profile: GstMappingProfile;
  applicable: boolean;
  matchedSheets: number;
  unmatchedSheets: number;
  missingHeaders: string[];
  selected: boolean;
}

export interface GstMappingPreviewRequest {
  profileId?: string | null;
  definition?: string | null;
  page?: number;
  pageSize?: number;
  errorsOnly?: boolean;
}

export interface GstMappedRecord {
  sheet: string;
  rowIndex: number;
  parentRowIndex?: number | null;
  /** LINE | DOCUMENT | SUMMARY. */
  recordKind: string;
  fields: Record<string, string | number | boolean | null>;
  errors: string[];
  warnings: string[];
}

export interface GstRateTotal {
  rate?: number | null;
  count: number;
  taxableValue: number;
  igst: number;
  cgst: number;
  sgst: number;
  cess: number;
}

export interface GstMappingPreview {
  profileId?: string | null;
  profileName?: string | null;
  inputRows: number;
  recordCount: number;
  recordsWithErrors: number;
  recordsWithWarnings: number;
  messages: { sheet?: string | null; severity: string; message: string }[];
  byRate: GstRateTotal[];
  columns: string[];
  page: number;
  pageSize: number;
  totalCount: number;
  items: GstMappedRecord[];
}

export interface GstSubmissionStreamRequest {
  documentType: string;
  sheetNames: string[];
  summaryBasis?: string | null;
}

/** One document, one scope, several streams — one submission per stream. */
export interface CreateGstSubmissionBatchRequest {
  sourceDocumentId: string;
  registrationId: string;
  taxPeriodStart: string;
  taxPeriodEnd: string;
  sourceRef: string;
  streams: GstSubmissionStreamRequest[];
  confirmSupersede: boolean;
  revisionReason?: string | null;
  revisionNote?: string | null;
}

/** What creating a submission for a scope would do: add alongside (no `existing`) or replace revision N. */
export interface GstSubmissionScopeCheck {
  existing?: GstDataSubmission | null;
  nextSubmissionNo: number;
  /**
   * Whether the live revision was parsed from THIS document: REPEAT (same
   * sheets — the parse is refused), CORRECTION (different sheets — a new
   * revision, no reason needed) or NONE.
   */
  reparse: 'NONE' | 'REPEAT' | 'CORRECTION';
}

/** One live submission a received document feeds. */
export interface GstDocumentFeed {
  submissionId: string;
  submissionNo: number;
  documentType: string;
  sourceRef: string;
  taxPeriodStart: string;
  taxPeriodEnd: string;
}

export interface GstStagingCell {
  v: string | number | boolean | null;
  /** s string · n number · d date · b boolean · e error. */
  t: string;
}

export interface GstStagingRow {
  id: string;
  sheetName: string;
  rowIndex: number;
  /** TITLE | HEADER | DATA | CONTINUATION | TRAILER | IGNORED. */
  rowKind: string;
  parentRowIndex?: number | null;
  coercionError?: string | null;
  cells: GstStagingCell[];
}

export interface GstStagingSearchRequest {
  page?: number;
  pageSize?: number;
  sheetName?: string;
  rowKind?: string;
}

export const GST_SOURCE_DOCUMENT_KINDS: ReadonlyArray<{ value: string; label: string; group: 'Outward' | 'Exports' | 'Inward' | 'Other' }> = [
  { value: 'SALES_REGISTER', label: 'Sales register', group: 'Outward' },
  { value: 'SALES_RETURN', label: 'Sales return', group: 'Outward' },
  { value: 'B2B', label: 'B2B invoices', group: 'Outward' },
  { value: 'B2CS', label: 'B2C (small)', group: 'Outward' },
  { value: 'B2CL', label: 'B2C (large)', group: 'Outward' },
  { value: 'HSN_SUMMARY', label: 'HSN summary', group: 'Outward' },
  { value: 'CREDIT_NOTE_OUTWARD', label: 'Credit note issued', group: 'Outward' },
  { value: 'DEBIT_NOTE_OUTWARD', label: 'Debit note issued', group: 'Outward' },
  { value: 'GSTR1_EXPORT', label: 'GSTR-1 export (Tally / accounting software)', group: 'Exports' },
  { value: 'ACCOUNTING_GST_REPORT', label: 'Accounting software GST report (all sheets)', group: 'Exports' },
  { value: 'PURCHASE_REGISTER', label: 'Purchase register', group: 'Inward' },
  { value: 'PURCHASE_RETURN', label: 'Purchase return', group: 'Inward' },
  { value: 'CREDIT_NOTE_INWARD', label: 'Credit note received', group: 'Inward' },
  { value: 'DEBIT_NOTE_INWARD', label: 'Debit note received', group: 'Inward' },
  { value: 'OTHER', label: 'Other', group: 'Other' },
];
